import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ShelfOutletContext } from "./MyShelfScreen";
import { ReadingListRailRow } from "../../components/lists/ReadingListRailRow";
import { NewListCard } from "../../components/lists/NewListCard";
import { CreateReadingListWizard } from "../../components/lists/CreateReadingListWizard";
import { useReadingListsShelf } from "./hooks/useReadingListsShelf";
import { FilterTagButton } from "../../components/buttons/FilterTag";
import {
  ReadingListSortMenu,
  SortDirection,
  SortOption,
} from "../../components/lists/ReadingListSortMenu";
import { TextIcon } from "../../components/icons/TextIcon";
import { ClockIcon } from "../../components/icons/ClockIcon";
import { CalendarIcon } from "../../components/icons/CalendarIcon";
import {
  ReadingListsShelfCounts,
  ReadingListsShelfFilter,
  ReadingListsShelfSort,
} from "../../api/lists";

type ListsFilter = "all" | "yours" | "following";
const SEARCH_DEBOUNCE_MS = 250;

const FILTER_TO_BACKEND: Record<ListsFilter, ReadingListsShelfFilter> = {
  all: "all",
  yours: "own",
  following: "following",
};

export default function ReadingListsShelfScreen() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { setSearchConfig } = useOutletContext<ShelfOutletContext>();

  const [filter, setFilter] = useState<ListsFilter>("all");
  const [sort, setSort] = useState<ReadingListsShelfSort>("created_time");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);

  // Debounce the search input so each keystroke doesn't issue a new page-1
  // fetch. The trimmed term is what feeds the query key.
  useEffect(() => {
    const id = setTimeout(
      () => setSearchTerm(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [searchInput]);

  // Push search up to the My Shelf top bar (same pattern as Library).
  useEffect(() => {
    setSearchConfig({
      placeholder: t("screens.shelf.readingLists.layout.searchPlaceholder"),
      onSearch: setSearchInput,
    });
    return () => setSearchConfig(null);
  }, [setSearchConfig, t]);

  const sortOptions = useMemo<SortOption[]>(
    () => [
      {
        id: "created_time",
        name: t("screens.shelf.readingLists.layout.sortCreatedTime"),
        Icon: CalendarIcon,
        defaultDirection: "desc",
      },
      {
        id: "update_time",
        name: t("screens.shelf.readingLists.layout.sortUpdateTime"),
        Icon: ClockIcon,
        defaultDirection: "desc",
      },
      {
        id: "alphabetical",
        name: t("screens.shelf.readingLists.layout.sortAlphabetical"),
        Icon: TextIcon,
        defaultDirection: "asc",
      },
    ],
    [t],
  );

  const {
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    lists,
    counts,
  } = useReadingListsShelf({
    filter: FILTER_TO_BACKEND[filter],
    q: searchTerm,
    sort,
    direction: sortDirection,
  });

  // Watch for the IntersectionObserver sentinel at the bottom of the list.
  // When it scrolls into view we ask for the next page.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchNextPage();
      },
      { rootMargin: "120px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelect = useCallback(
    (listId: number) => {
      // Navigate to the nested shelf URL so the My Shelf tabs stay visible
      // when reading inside the user's own shelf. The top-level
      // /reading-lists/<id> URL is reserved for canonical public links
      // (renders the same screen without the shelf chrome).
      navigate(`/my-shelf/reading-lists/${listId}`);
    },
    [navigate],
  );

  const handleSortChange = useCallback(
    (key: string, direction: SortDirection) => {
      setSort(key as ReadingListsShelfSort);
      setSortDirection(direction);
    },
    [],
  );

  if (error) {
    return (
      <Flex direction="column" align="center" gap="8px" py="48px">
        <Text color="error.300" fontSize="14px">
          {t("screens.shelf.readingLists.error.title")}
        </Text>
        <Text color="neutral.400" fontSize="12px">
          {t("screens.shelf.readingLists.error.retry")}
        </Text>
      </Flex>
    );
  }

  // First mount only — `placeholderData: keepPreviousData` keeps the list
  // populated across (filter, q) refetches, so isLoading reflects "no data
  // ever, period" and the spinner replaces only the list body.
  const showInitialSpinner = isLoading && lists.length === 0;
  const isEmpty = !isLoading && lists.length === 0;
  const emptyMessageKey = searchTerm
    ? "screens.shelf.readingLists.layout.noResults"
    : filter === "yours"
      ? "screens.shelf.readingLists.layout.noOwnLists"
      : "screens.shelf.readingLists.layout.noResults";

  return (
    <>
      <Flex direction="column">
        <FilterBar
          filter={filter}
          onFilterChange={setFilter}
          counts={counts}
          sort={sort}
          sortDirection={sortDirection}
          sortOptions={sortOptions}
          onSortChange={handleSortChange}
          onCreate={() => setWizardOpen(true)}
        />
        {/*
          Vertical feed of full-width horizontal cards (per Figma node
          3120:59436). Was a 1/2/3-column SimpleGrid; now a single
          column. Each card already lays out horizontally (cover-stack
          on left, title + description + stats on right) — the grid was
          the only thing making them render as small tiles. The "Create
          a list" tile ships LAST (was first) to match the design.
        */}
        <Flex
          direction="column"
          // 20px between cards — per Figma. Was 12; the larger gap reads
          // as a deliberate beat rather than a stack collision when the
          // rows themselves are taller and chrome-heavier than the
          // earlier compact-tile spec.
          gap="20px"
          px="8px"
          pb="16px"
        >
          {!showInitialSpinner &&
            !isEmpty &&
            lists.map((list) => (
              <ReadingListRailRow
                key={list.id}
                list={list}
                onClick={() => handleSelect(list.id)}
              />
            ))}
          {/*
            "Create a list" tile is hidden while the initial query is loading
            — without any real lists to anchor it, the lone placeholder reads
            as a glitch rather than a deliberate add-list affordance. Surface
            it once we have data (including the empty state, where it's the
            user's only path to getting started).
          */}
          {!showInitialSpinner && (
            <NewListCard
              label={t("screens.shelf.readingLists.layout.createListTile")}
              onClick={() => setWizardOpen(true)}
            />
          )}
        </Flex>
        {showInitialSpinner && (
          <Flex justify="center" py="24px">
            <Spinner color="dune.dune" />
          </Flex>
        )}
        {isEmpty && (
          <Text
            fontSize="13px"
            color="neutral.500"
            textAlign="center"
            py="24px"
          >
            {t(emptyMessageKey)}
          </Text>
        )}
        {hasNextPage && (
          <Box
            ref={sentinelRef}
            py="12px"
            display="flex"
            justifyContent="center"
          >
            {isFetchingNextPage && <Spinner size="sm" color="dune.700" />}
          </Box>
        )}
      </Flex>
      <CreateReadingListWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />
    </>
  );
}

interface FilterBarProps {
  filter: ListsFilter;
  onFilterChange: (filter: ListsFilter) => void;
  counts?: ReadingListsShelfCounts;
  sort: ReadingListsShelfSort;
  sortDirection: SortDirection;
  sortOptions: SortOption[];
  onSortChange: (key: string, direction: SortDirection) => void;
  // Opens the create-list wizard. Lives on the FilterBar (per Figma node
  // 3120:59436) rather than in some floating CTA so it's always visible
  // alongside the filter tabs and the eye is drawn to "create" right at
  // the moment the user is scanning their existing lists.
  onCreate: () => void;
}

function FilterBar({
  filter,
  onFilterChange,
  counts,
  sort,
  sortDirection,
  sortOptions,
  onSortChange,
  onCreate,
}: FilterBarProps) {
  const [t] = useTranslation();
  const showCounts = counts != null;
  // Extracted into JSX vars so we can place the same content at two
  // different positions per breakpoint without duplicating callbacks or
  // i18n lookups. Both copies are mounted at all times — only one is
  // visible per breakpoint via Chakra's responsive `display`. Each is a
  // distinct React element with its own internal popover state so the
  // mounted-but-hidden copy doesn't interfere with the visible one.
  const createButton = (
    <Button
      variant="primary"
      size="sm"
      // Pinned to 36px so the CTA's outer height matches the
      // FilterTagButton pills sitting to its left — the Chakra default
      // 'sm' button height runs shorter than the filter pill's 36px,
      // which visually left the CTA looking like a sub-tier action
      // even though it's the most important affordance on the row.
      h="36px"
      // 12px corners — softer than the variant default pill but still
      // squarer than the FilterTagButton's fully-rounded edges, so the
      // CTA reads as a deliberate sibling rather than a fourth pill.
      borderRadius="12px"
      flexShrink={0}
      onClick={onCreate}
      leftIcon={
        <Box
          as="svg"
          viewBox="0 0 24 24"
          w="16px"
          h="16px"
          aria-hidden="true"
        >
          <path
            d="M12 5V19M5 12H19"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </Box>
      }
    >
      {t("screens.shelf.readingLists.layout.createListButton")}
    </Button>
  );
  const sortMenu = (
    <ReadingListSortMenu
      sortKey={sort}
      sortDirection={sortDirection}
      options={sortOptions}
      onChange={onSortChange}
      ariaLabel={t("screens.shelf.readingLists.layout.sortAriaLabel")}
    />
  );

  return (
    <Flex
      direction="column"
      flexShrink={0}
      bg="charcoal.charcoal"
      // Tighter screen-edge padding on mobile.
      px={{ base: "8px", md: "16px" }}
      // Per Figma:
      //   pt = 30px — distance from the tabs divider above the FilterBar
      //               to the first row of filters.
      //   pb = 24px — distance from the FilterBar's last row down to the
      //               first list card.
      pt="30px"
      pb="24px"
      gap={{ base: "12px", md: 0 }}
    >
      {/*
        Row 1 — desktop: tabs + divider + Create CTA on the left, sort
        menu pinned to the right. Mobile: only the tab strip lives here,
        scrolling horizontally on overflow; the Create CTA + sort menu
        get their own row below.
      */}
      <Flex direction="row" align="center" justify="space-between" gap="8px">
        <Flex
          direction="row"
          gap="8px"
          align="center"
          wrap={{ base: "nowrap", md: "wrap" }}
          overflowX={{ base: "auto", md: "visible" }}
          flex="1 1 auto"
          minW={0}
          // Scrollbar is purely visual noise here — content is short
          // enough that the partial chip hanging off the right edge
          // reads as the affordance.
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box flexShrink={0}>
            <FilterTagButton
              text={t("screens.shelf.readingLists.layout.allTab")}
              isActive={filter === "all"}
              isFiltering={showCounts}
              activeFilters={counts?.all}
              onClick={() => onFilterChange("all")}
            />
          </Box>
          <Box flexShrink={0}>
            <FilterTagButton
              text={t("screens.shelf.readingLists.layout.yoursTab")}
              isActive={filter === "yours"}
              isFiltering={showCounts}
              activeFilters={counts?.own}
              onClick={() => onFilterChange("yours")}
            />
          </Box>
          <Box flexShrink={0}>
            <FilterTagButton
              text={t("screens.shelf.readingLists.layout.followingTab")}
              isActive={filter === "following"}
              isFiltering={showCounts}
              activeFilters={counts?.following}
              onClick={() => onFilterChange("following")}
            />
          </Box>
          {/* Divider + Create CTA inline with tabs only on desktop. On
              mobile the CTA jumps to the second row below. */}
          <Box
            alignSelf="stretch"
            width="1px"
            bg="transparent.white.20"
            mx="4px"
            flexShrink={0}
            display={{ base: "none", md: "block" }}
          />
          <Box display={{ base: "none", md: "block" }} flexShrink={0}>
            {createButton}
          </Box>
        </Flex>
        {/* Sort menu pinned to the right — desktop only. */}
        <Box display={{ base: "none", md: "block" }} flexShrink={0}>
          {sortMenu}
        </Box>
      </Flex>

      {/*
        Row 2 — mobile only. Holds Create CTA on the left and the sort
        menu on the right; row 1 above is the scrolling filter strip.
        Create CTA expands to fill the row's remaining width so it
        reads as the primary mobile action instead of an arbitrarily-
        sized pill next to the sort dropdown.
      */}
      <Flex
        direction="row"
        align="center"
        justify="space-between"
        gap="8px"
        display={{ base: "flex", md: "none" }}
      >
        <Box
          flex="1"
          minW={0}
          // Push the button to fill its container — the createButton
          // JSX is reused on desktop where it must size to content, so
          // we override width here at the wrapper level rather than
          // baking width into the button itself.
          sx={{ "& > button": { width: "100%", maxWidth: "100%" } }}
        >
          {createButton}
        </Box>
        <Box flexShrink={0}>{sortMenu}</Box>
      </Flex>
    </Flex>
  );
}
